import * as PrettyNum from '@ebiz/pretty-num';
import RouterService from '@ember/routing/router-service';
import Service from '@ember/service';
import { service } from '@ember/service';
import StateService from './state';
import Owner from '@ember/owner';

// This interface and class abstract the use of the routing service.
// Call setParameterWrangler to use a stub implementation in unit tests.
export interface QueryParameterWrangler {
  getParameter(name: string): string | undefined;
  setParameter(name: string, value: string): void;
  pruneParameters(parms: Record<string, string>, deletions: string[]): void;
}

class QueryParameterWranglerViaRoute implements QueryParameterWrangler {
  router: RouterService;
  constructor(router: RouterService) {
    this.router = router;
  }
  getParameter(name: string): string | undefined {
    return this.router.currentRoute?.queryParams[name] as string | undefined;
  }
  setParameter(name: string, value: string): void {
    const existing = this.router.currentRoute?.queryParams;
    if (existing) {
      const queryParams = { ...existing };
      queryParams[name] = value;
      this.router.replaceWith(this.router.currentRoute?.name, {
        queryParams: queryParams,
      });
    }
  }
  pruneParameters(parms: Record<string, string>, deletions: string[]): void {
    const existing = this.router.currentRoute?.queryParams;
    if (existing) {
      const queryParams = { ...existing };
      for (const parm of deletions) {
        queryParams[parm] = undefined;
      }
      for (const parm in parms) {
        queryParams[parm] = parms[parm];
      }
      this.router.replaceWith(this.router.currentRoute?.name, {
        queryParams: queryParams,
      });
    }
  }
}

export default class URLService extends Service {
  @service declare router: RouterService;
  @service declare state: StateService;
  _parameterWrangler: QueryParameterWrangler;

  constructor(owner: Owner) {
    super(owner);
    this._parameterWrangler = new QueryParameterWranglerViaRoute(this.router);
  }

  setParameterWrangler(stub: QueryParameterWrangler): QueryParameterWrangler {
    const oldWrangler = this._parameterWrangler;
    this._parameterWrangler = stub;
    return oldWrangler;
  }

  // The following method must be called after the route has been made available to the router service.
  setQueryParams(param: string | undefined): void {
    const updates: Record<string, string> = {};
    const deletions: string[] = [];

    const partNumber = this.state.partNumber;
    const mult = PrettyNum.numberToUserDisplay(this.state.multiplier, 'Standard', 8);
    const sclock = PrettyNum.numberToUserDisplay(this.state.systemClock, 'Engineering', 12);
    const fin = PrettyNum.numberToUserDisplay(this.state.inputClockFrequency, 'Engineering', 8);
    const ftw = isNaN(this.state.ftw) ? '' : this.state.ftw.toString(16).toUpperCase();
    const tof = PrettyNum.numberToUserDisplay(this.state.targetFrequency, 'Engineering', 12);
    const rso = this.state.signalDisplayOptions;
    const harmonicDB = PrettyNum.numberToUserDisplay(this.state.harmonicDB, 'Standard', 8);

    const useFilters = this.state.displayFilters ? '1' : '0';
    const fType = PrettyNum.numberToUserDisplay(this.state.filterType, 'Standard', 8);
    const fTop = PrettyNum.numberToUserDisplay(this.state.filterTopology, 'Standard', 8);
    const fOrder = PrettyNum.numberToUserDisplay(this.state.filterOrder, 'Standard', 8);
    const fc = PrettyNum.numberToUserDisplay(this.state.fc, 'Engineering', 8);

    const fRipple = PrettyNum.numberToUserDisplay(this.state.passbandRipple, 'Standard', 8);
    const fAtten = PrettyNum.numberToUserDisplay(this.state.stopbandAtten, 'Standard', 8);
    const isDebug = this.state.isDebug ?? false;

    // check if there are query params and update URL accordingly
    const queryParams = this.state._suppliedParms;
    if (queryParams && Object.keys(queryParams).length > 0) {
      if (param) {
        // manually updated the field
        if (this.state.ftwMoreRecentThanTof && param !== 'tof') {
          updates['ftw'] = ftw;
        }
        if (this.state.finMoreRecentThanSClock && param !== 'fin') {
          updates['fin'] = fin;
        }

        switch (param) {
          case 'part':
            updates['part'] = partNumber;
            updates['mult'] = mult;
            updates['sclock'] = sclock;
            updates['fc'] = fc;
            deletions.push('fin');
            break;
          case 'fin':
            updates['fin'] = fin;
            deletions.push('sclock');
            break;
          case 'mult':
            updates['mult'] = mult;
            break;
          case 'sclock':
            updates['sclock'] = sclock;
            updates['mult'] = mult;
            deletions.push('fin');
            break;
          case 'tof':
            updates['tof'] = tof;
            deletions.push('ftw');
            break;
          case 'ftw':
            updates['ftw'] = ftw;
            deletions.push('tof');
            break;
          case 'rso':
            updates['rso'] = rso;
            break;
          case 'harmonicDB':
            updates['harmonicDB'] = harmonicDB;
            break;
          case 'useFilters':
            updates['useFilters'] = useFilters;
            if (useFilters === '1') {
              updates['fType'] = fType;
              updates['fTop'] = fTop;
              updates['fOrder'] = fOrder;
              updates['fc'] = fc;
              if (fTop === '1') {
                updates['fRipple'] = fRipple;
                deletions.push('fAtten');
              } else if (fTop === '2') {
                updates['fAtten'] = fAtten;
                deletions.push('fRipple');
              } else {
                deletions.push('fAtten');
                deletions.push('fRipple');
              }
            } else {
              deletions.push('fType');
              deletions.push('fTop');
              deletions.push('fOrder');
              deletions.push('fc');
              deletions.push('fAtten');
              deletions.push('fRipple');
            }
            break;
          default:
            break;
        }
      } else {
        // copy/paste/manually updated the URL
        for (const [key, value] of Object.entries(queryParams)) {
          if (value) {
            switch (key) {
              case 'partId':
                updates['part'] = partNumber;
                break;
              case 'qmult':
                updates['mult'] = mult;
                break;
              case 'qsclock':
                updates['sclock'] = sclock;
                break;
              case 'qfin':
                updates['fin'] = fin;
                break;
              case 'qtof':
                updates['tof'] = tof;
                break;
              case 'qftw':
                updates['ftw'] = ftw;
                break;
              case 'qrso':
                updates['rso'] = rso;
                break;
              case 'harmonicDB':
                updates['harmonicDB'] = harmonicDB;
                break;
              case 'useFilters':
                updates['useFilters'] = useFilters;
                break;
              case 'qfType':
                updates['fType'] = fType;
                break;
              case 'qfTop':
                updates['fTop'] = fTop;
                break;
              case 'qfOrder':
                updates['fOrder'] = fOrder;
                break;
              case 'qfc':
                updates['fc'] = fc;
                break;
              case 'qfAtten':
                updates['fAtten'] = fAtten;
                break;
              case 'qfRipple':
                updates['fRipple'] = fRipple;
                break;
              case 'qdebug':
                updates['debug'] = isDebug ? 'true' : 'false';
                break;
              default:
                break;
            }
          }
        }
      }
    } else {
      // set defaults
      updates['part'] = partNumber;
      updates['mult'] = mult;
      updates['sclock'] = sclock;
      updates['tof'] = tof;
      updates['rso'] = rso;
      updates['harmonicDB'] = harmonicDB;
      updates['useFilters'] = useFilters;

      // create/assign values to _suppliedParms
      const suppliedParams = {
        partId: partNumber,
        qmult: mult,
        qsclock: sclock,
        qfin: fin,
        qtof: tof,
        qftw: ftw,
        qrso: rso,
        harmonicDB: harmonicDB,
        useFilters: useFilters,
      };
      this.state._suppliedParms = { ...suppliedParams };
    }

    if (!isDebug) {
      deletions.push('debug');
    }

    this._parameterWrangler.pruneParameters(updates, deletions);
  }
}

declare module '@ember/service' {
  interface Registry {
    url: URLService;
  }
}
